import { useCallback, useState } from "react"

import { CommentatorReaction } from "@/components/commentator-reaction"
import { RevealBlock } from "@/components/reveal-block"
import { SegmentHeader } from "@/components/segment-header"
import { SlamBlock } from "@/components/slam-block"
import { SmallPortrait } from "@/components/small-portrait"
import {
  DEMO_ANIMATION_DATA,
  DEMO_AWAY_HUE,
  DEMO_HOME_HUE,
} from "@/compositions/mock-data"
import { MiniEventLog } from "@/pages/matches/key-plays/mini-event-log"
import { MiniReplayCard } from "@/pages/matches/key-plays/mini-replay-card"

import { CommentaryPanel } from "./commentary-panel"
import { COMMENTATORS } from "./commentators"
import { HomepageCta } from "./homepage-cta"
import { NeonTraceTitle } from "./neon-trace-title"

const INTRO_LINES = [
  {
    commentatorKey: "grak_ironjaw",
    name: "Grak Ironjaw",
    mood: "neutral",
    line: "You're new here. That's fine. Everyone's new once. Some of 'em even survive.",
    side: "left" as const,
  },
  {
    commentatorKey: "vaeltharion_the_unimpressed",
    name: "Vaeltharion",
    mood: "smug",
    line: "It's American football with orcs, elves, and the undead. They removed the rule about not killing people. Dice decide everything.",
    side: "right" as const,
  },
  {
    commentatorKey: "skrix_dataclack",
    name: "Skrix Dataclack",
    mood: "excited",
    line: "Nuffle Club runs the matches for you, yes-yes! Draft a team, the engine simulates every block and pass, you watch the replay!",
    side: "left" as const,
  },
  {
    commentatorKey: "grak_ironjaw",
    name: "Grak Ironjaw",
    mood: "excited",
    line: "Enough talk. Watch this.",
    side: "right" as const,
  },
]

// Post-replay: no commentary — let the replay speak for itself.

// Extract starting snapshot from first event's pitchSnapshot
const INITIAL_SNAPSHOT = DEMO_ANIMATION_DATA.events[0].pitchSnapshot!

const TIMING = {
  portraitStrip: 0.1, // commentator portraits
  introStart: 0.2, // first intro commentary line
  introStagger: 0.15, // between intro lines
  replayCard: 0.15, // broadcast monitor
  keyPlayImage: 0.2, // full-bleed key play reveal
  keyPlayTitle: 0.3, // "THAT'S NUFFLE CLUB." slam-in
} as const

interface BroadcastOpenSectionProps {
  portraitUrls: Record<string, string>
  keyPlayImageUrl?: string | null
}

export function BroadcastOpenSection({
  portraitUrls,
  keyPlayImageUrl,
}: BroadcastOpenSectionProps) {
  const [currentEventIndex, setCurrentEventIndex] = useState(-1)
  const handleEventChange = useCallback((idx: number) => {
    setCurrentEventIndex(idx)
  }, [])

  return (
    <>
      {/* ── Desk intro ── */}
      <div className="mx-auto max-w-5xl px-4">
        <RevealBlock>
          <SegmentHeader label="Live from the Broadcast Booth" />
        </RevealBlock>

        <RevealBlock delay={TIMING.portraitStrip}>
          <div className="mt-8 flex items-center justify-center gap-4">
            {COMMENTATORS.map((c) => (
              <SmallPortrait
                key={c.key}
                commentatorKey={c.key}
                name={c.name}
                portraitUrl={portraitUrls[`${c.key}_neutral`] ?? null}
              />
            ))}
          </div>
        </RevealBlock>
      </div>

      {/* ── Intro commentary ── */}
      <CommentaryPanel>
        {INTRO_LINES.map((entry, i) => (
          <SlamBlock
            key={entry.commentatorKey}
            from={entry.side}
            delay={TIMING.introStart + i * TIMING.introStagger}
          >
            <CommentatorReaction
              commentatorKey={entry.commentatorKey}
              name={entry.name}
              mood={entry.mood}
              line={entry.line}
              portraitUrl={
                portraitUrls[`${entry.commentatorKey}_${entry.mood}`] ?? null
              }
              side={entry.side}
            />
          </SlamBlock>
        ))}
      </CommentaryPanel>

      {/* ── Pitch replay with blown-out backdrop ── */}
      <RevealBlock delay={TIMING.replayCard}>
        <div
          className="relative py-12 md:py-20"
          style={{
            width: "100vw",
            marginLeft: "calc(-50vw + 50%)",
            overflow: "hidden",
          }}
        >
          {/* Top neon rule */}
          <div
            className="absolute top-0 right-0 left-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 5%, oklch(0.8 0.3 145 / 0.4) 25%, oklch(0.85 0.28 85 / 0.6) 50%, oklch(0.8 0.3 145 / 0.4) 75%, transparent 95%)",
              boxShadow:
                "0 0 8px oklch(0.85 0.28 85 / 0.3), 0 0 20px oklch(0.8 0.3 145 / 0.15)",
              zIndex: 10,
            }}
          />

          {/* Bottom neon rule */}
          <div
            className="absolute right-0 bottom-0 left-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 5%, oklch(0.8 0.3 145 / 0.4) 25%, oklch(0.85 0.28 85 / 0.6) 50%, oklch(0.8 0.3 145 / 0.4) 75%, transparent 95%)",
              boxShadow:
                "0 0 8px oklch(0.85 0.28 85 / 0.3), 0 0 20px oklch(0.8 0.3 145 / 0.15)",
              zIndex: 10,
            }}
          />

          {/* Tilted pitch checkerboard — rotated, blurred, behind everything */}
          <div
            className="pointer-events-none absolute"
            style={{
              inset: "-80%",
              background: "oklch(0.26 0.08 145)",
              backgroundImage:
                "conic-gradient(oklch(0.22 0.06 145) 0.25turn, oklch(0.28 0.09 145) 0.25turn 0.5turn, oklch(0.22 0.06 145) 0.5turn 0.75turn, oklch(0.28 0.09 145) 0.75turn)",
              backgroundSize: "120px 120px",
              transform: "rotate(-30deg)",
              filter: "blur(3px)",
            }}
          />

          {/* Frosted glass tint */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: "oklch(0.09 0.01 55 / 0.15)",
            }}
          />

          {/* Vignette — fades edges to broadcast bg */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 50% 50%, transparent 40%, oklch(0.09 0.01 55 / 0.5) 65%, oklch(0.09 0.01 55) 90%)",
            }}
          />

          {/* Replay monitor — centered, backlit by the pitch */}
          <div className="relative mx-auto max-w-5xl px-4">
            <div
              className="relative"
              style={{
                boxShadow:
                  "0 0 80px oklch(0.35 0.15 145 / 0.6), 0 0 160px oklch(0.30 0.12 145 / 0.4), 0 0 240px oklch(0.26 0.08 145 / 0.2), 0 8px 32px oklch(0 0 0 / 0.5)",
              }}
            >
              <div
                style={{
                  aspectRatio: "26 / 15",
                }}
              >
                <MiniReplayCard
                  replaySnapshot={INITIAL_SNAPSHOT}
                  replayEvents={DEMO_ANIMATION_DATA.events}
                  playersData={DEMO_ANIMATION_DATA.players}
                  homeTeamId={DEMO_ANIMATION_DATA.homeTeamId}
                  homeTeamHue={DEMO_HOME_HUE}
                  awayTeamHue={DEMO_AWAY_HUE}
                  paused={false}
                  onEventChange={handleEventChange}
                />
              </div>

              {/* Narrative event log — scrolls with the replay */}
              <MiniEventLog
                events={DEMO_ANIMATION_DATA.events}
                currentEventIndex={currentEventIndex}
                homeTeamId={DEMO_ANIMATION_DATA.homeTeamId}
                homeTeamHue={DEMO_HOME_HUE}
                awayTeamHue={DEMO_AWAY_HUE}
              />
            </div>
          </div>
        </div>
      </RevealBlock>

      {/* ── Key play hero — full-bleed image ── */}
      <RevealBlock delay={TIMING.keyPlayImage}>
        <div
          className="relative flex items-end justify-center overflow-hidden"
          style={{
            width: "100vw",
            marginLeft: "calc(-50vw + 50%)",
            aspectRatio: "1920 / 480",
          }}
        >
          {keyPlayImageUrl && (
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage: `url('${keyPlayImageUrl}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, oklch(0.04 0.01 55 / 0.05) 0%, oklch(0.04 0.01 55 / 0.1) 40%, oklch(0.04 0.01 55 / 0.6) 70%, oklch(0.04 0.01 55 / 0.95) 100%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "repeating-linear-gradient(0deg, oklch(0 0 0 / 0.06) 0px, oklch(0 0 0 / 0.06) 1px, transparent 1px, transparent 3px)",
            }}
          />
          <SlamBlock
            from="bottom"
            delay={TIMING.keyPlayTitle}
            className="w-full"
          >
            <div className="relative z-10 px-4 pb-6 md:pb-10">
              <NeonTraceTitle
                id="kp"
                text="THAT'S NUFFLE CLUB."
                viewBox="0 0 2200 170"
                fontSize={150}
                letterSpacing={14}
                glowBlur={4}
                textGlowBlur={6}
                goldStrokeWidth={10}
                greenStrokeWidth={8}
                ariaLabel="That's Nuffle Club."
              />
            </div>
          </SlamBlock>
        </div>
      </RevealBlock>

      {/* ── Early CTA ── */}
      <RevealBlock>
        <HomepageCta />
      </RevealBlock>
    </>
  )
}
